/**
 * WorkspaceAdmissionPanel — Read-only admission outcome inspector
 *
 * Surfaces admission outcomes (ADMITTED | REPAIRED | REJECTED | PENDING),
 * rationale, gaps, admission artifact ref, and domain-specific boundaries.
 *
 * Gap extension v1: net-new pane not covered by ProposalMemoryPane or
 * AdmissibilityChainPane. ProposalMemoryPane surfaces per-proposal
 * admissibilityStatus only. This pane surfaces the full GovernedWorkUnit
 * admission outcome including gaps, rationale, and domain boundaries.
 *
 * SAFETY: Read-only, proof-only. No execution authority, no mutation,
 * no legal conclusions, no autonomous action.
 */

import React from 'react';
import type { AdmissionPaneModel, AdmissionGap, AdmissionDomainBoundary } from '../../modules/governanceEventStream/operationalRendering/types.js';
import { NEUTRAL, SPACING, TYPOGRAPHY, SEMANTIC } from '../../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface WorkspaceAdmissionPanelProps {
  model: AdmissionPaneModel;
  onSelectWorkUnit?: (workUnitId: string) => void;
}

function outcomeColor(outcome: string) {
  if (outcome === 'ADMITTED') return SEMANTIC.stabilized;
  if (outcome === 'REPAIRED') return SEMANTIC.neutral;
  if (outcome === 'REJECTED') return SEMANTIC.refusal;
  if (outcome === 'PENDING') return SEMANTIC.neutral;
  return SEMANTIC.neutral;
}

function gapSeverityColor(severity: string): string {
  if (severity === 'critical') return SEMANTIC.refusal.text;
  if (severity === 'high') return '#e07030';
  if (severity === 'medium') return NEUTRAL.text500;
  return NEUTRAL.text400;
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div style={{
      fontSize: TYPOGRAPHY.micro.size,
      fontWeight: 600,
      color: NEUTRAL.text500,
      textTransform: 'uppercase' as const,
      letterSpacing: 0.4,
      marginBottom: SPACING.sp2,
    }}>
      {title}
    </div>
  );
}

function RefList({ items, label }: { items: string[], label: string }) {
  if (!items.length) {
    return (
      <div style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300, fontStyle: 'italic' }}>
        No {label}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sp1 }}>
      {items.map((item, i) => (
        <div key={i} style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontFamily: TYPOGRAPHY.mono.family,
          color: NEUTRAL.text400,
          backgroundColor: NEUTRAL.bg700,
          padding: `${SPACING.sp1}px ${SPACING.sp2}px`,
          borderRadius: 2,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {item}
        </div>
      ))}
    </div>
  );
}

function GapRow({ gap }: { gap: AdmissionGap }) {
  return (
    <div style={{
      padding: `${SPACING.sp1}px ${SPACING.sp2}px`,
      backgroundColor: NEUTRAL.bg700,
      borderRadius: 2,
      borderLeft: `2px solid ${gapSeverityColor(gap.severity)}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: SPACING.sp2,
    }}>
      <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text400, flex: 1 }}>
        {gap.description}
      </span>
      <span style={{
        fontSize: TYPOGRAPHY.micro.size,
        fontWeight: 600,
        color: gapSeverityColor(gap.severity),
        textTransform: 'uppercase' as const,
        letterSpacing: 0.3,
        flexShrink: 0,
      }}>
        {gap.severity}
      </span>
      {gap.resolvable && (
        <span style={{
          fontSize: TYPOGRAPHY.micro.size,
          color: SEMANTIC.stabilized.text,
          backgroundColor: SEMANTIC.stabilized.chip,
          padding: '1px 4px',
          borderRadius: 2,
          flexShrink: 0,
        }}>
          resolvable
        </span>
      )}
    </div>
  );
}

function BoundaryRow({ boundary }: { boundary: AdmissionDomainBoundary }) {
  return (
    <div style={{
      padding: `${SPACING.sp1}px ${SPACING.sp2}px`,
      backgroundColor: NEUTRAL.bg700,
      borderRadius: 2,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: SPACING.sp2,
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: TYPOGRAPHY.micro.size, fontWeight: 600, color: NEUTRAL.text500 }}>
          {boundary.domain}
          <span style={{ fontWeight: 400, color: NEUTRAL.text400, marginLeft: SPACING.sp2 }}>
            {boundary.boundaryType}
          </span>
        </div>
        <div style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text400, marginTop: 2 }}>
          {boundary.description}
        </div>
      </div>
      <span style={{
        fontSize: TYPOGRAPHY.micro.size,
        fontWeight: 600,
        color: boundary.enforced ? SEMANTIC.stabilized.text : SEMANTIC.refusal.text,
        backgroundColor: boundary.enforced ? SEMANTIC.stabilized.chip : SEMANTIC.refusal.chip,
        padding: '1px 5px',
        borderRadius: 2,
        textTransform: 'uppercase' as const,
        letterSpacing: 0.3,
        flexShrink: 0,
      }}>
        {boundary.enforced ? 'enforced' : 'not enforced'}
      </span>
    </div>
  );
}

export const WorkspaceAdmissionPanel: React.FC<WorkspaceAdmissionPanelProps> = ({ model, onSelectWorkUnit }) => {
  if (model.error) {
    return (
      <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, fontSize: TYPOGRAPHY.body.size }}>
        Admission error: {model.error}
      </div>
    );
  }

  const color = outcomeColor(model.outcome);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
        borderBottom: `1px solid ${NEUTRAL.border}`,
        backgroundColor: NEUTRAL.bg600,
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: TYPOGRAPHY.heading.size,
          fontWeight: TYPOGRAPHY.heading.weight,
          color: NEUTRAL.text600,
        }}>
          Admission
        </span>
        <span style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 700,
          color: color.text,
          backgroundColor: color.chip,
          padding: '2px 7px',
          borderRadius: 3,
          textTransform: 'uppercase' as const,
          letterSpacing: 0.4,
        }}>
          {model.outcome}
        </span>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: SPACING.sp3 }}>

        {/* Safety boundary strip */}
        <div style={{
          display: 'flex',
          gap: SPACING.sp2,
          flexWrap: 'wrap',
          marginBottom: SPACING.sp3,
          paddingBottom: SPACING.sp2,
          borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
        }}>
          {[
            'READ-ONLY',
            'PROOF-ONLY',
            'NO EXECUTION AUTHORITY',
          ].map(label => (
            <span key={label} style={{
              fontSize: TYPOGRAPHY.micro.size,
              fontWeight: 600,
              color: SEMANTIC.stabilized.text,
              backgroundColor: SEMANTIC.stabilized.chip,
              padding: '2px 6px',
              borderRadius: 3,
              textTransform: 'uppercase' as const,
              letterSpacing: 0.3,
            }}>
              {label}
            </span>
          ))}
        </div>

        {/* Work unit identity */}
        {(model.workUnitId || model.workUnitHash) && (
          <div style={{ marginBottom: SPACING.sp3 }}>
            <SectionHeader title="Work Unit" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sp1 }}>
              {model.workUnitId && (
                <div
                  style={{
                    fontSize: TYPOGRAPHY.caption.size,
                    fontFamily: TYPOGRAPHY.mono.family,
                    color: NEUTRAL.text500,
                    cursor: onSelectWorkUnit ? 'pointer' : 'default',
                    textDecoration: onSelectWorkUnit ? 'underline' : 'none',
                  }}
                  onClick={() => model.workUnitId && onSelectWorkUnit?.(model.workUnitId)}
                >
                  {model.workUnitId}
                </div>
              )}
              {model.workUnitHash && (
                <div style={{
                  fontSize: TYPOGRAPHY.micro.size,
                  fontFamily: TYPOGRAPHY.mono.family,
                  color: NEUTRAL.text300,
                }}>
                  hash: {model.workUnitHash.slice(0, 16)}…
                </div>
              )}
            </div>
          </div>
        )}

        {/* Timestamp */}
        {model.admissionTimestamp && (
          <div style={{ marginBottom: SPACING.sp3 }}>
            <SectionHeader title="Admitted At" />
            <div style={{
              fontSize: TYPOGRAPHY.micro.size,
              fontFamily: TYPOGRAPHY.mono.family,
              color: NEUTRAL.text400,
            }}>
              {model.admissionTimestamp}
            </div>
          </div>
        )}

        {/* Rationale */}
        <div style={{ marginBottom: SPACING.sp3 }}>
          <SectionHeader title="Rationale" />
          {model.rationale.length === 0 ? (
            <div style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300, fontStyle: 'italic' }}>
              No rationale recorded
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sp1 }}>
              {model.rationale.map((r, i) => (
                <div key={i} style={{
                  fontSize: TYPOGRAPHY.micro.size,
                  color: NEUTRAL.text400,
                  padding: `${SPACING.sp1}px ${SPACING.sp2}px`,
                  backgroundColor: NEUTRAL.bg700,
                  borderRadius: 2,
                }}>
                  {r}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Gaps */}
        <div style={{ marginBottom: SPACING.sp3 }}>
          <SectionHeader title={`Gaps (${model.gaps.length})`} />
          {model.gaps.length === 0 ? (
            <div style={{ fontSize: TYPOGRAPHY.micro.size, color: SEMANTIC.stabilized.text, fontStyle: 'italic' }}>
              No gaps detected
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sp1 }}>
              {model.gaps.map(gap => <GapRow key={gap.gapId} gap={gap} />)}
            </div>
          )}
        </div>

        {/* Admission artifact ref */}
        {model.admissionArtifactRef && (
          <div style={{ marginBottom: SPACING.sp3 }}>
            <SectionHeader title="Admission Artifact" />
            <div style={{
              fontSize: TYPOGRAPHY.micro.size,
              fontFamily: TYPOGRAPHY.mono.family,
              color: NEUTRAL.text400,
              backgroundColor: NEUTRAL.bg700,
              padding: `${SPACING.sp1}px ${SPACING.sp2}px`,
              borderRadius: 2,
            }}>
              {model.admissionArtifactRef}
            </div>
          </div>
        )}

        {/* Domain boundaries */}
        {model.domainBoundaries.length > 0 && (
          <div style={{ marginBottom: SPACING.sp3 }}>
            <SectionHeader title={`Domain Boundaries (${model.domainBoundaries.length})`} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sp1 }}>
              {model.domainBoundaries.map((b, i) => (
                <BoundaryRow key={i} boundary={b} />
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};
